import { RouterPath } from '@/routes/RouterPath';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as ViewModel from '@/core/presenter/viewModel/viewTypes';
import { showAlertPopup, showConfirmPopup } from '@/core/presenter/controller/PopupController';
import {
  MAX_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH_FOR_COMPLETE,
  MAX_PIN_LENGTH,
  MIN_PASSWORD_LENGTH_FOR_COMPLETE,
} from '@/consts';
import {
  tryChangePassword,
  tryCheckPassword,
  tryFetchAdminWalletBalance,
  trySetPin,
  tryWithdraw,
} from '@/core/presenter/controller/SettingController';
import { useStores } from '@/stores';
import { replaceNewLineToTag } from '@/utils';
import { checkSessionAndRedirect, tryLogout, tryPinCheck } from '@/core/presenter/controller/LoginController';
import { encryptPassword } from '@/core/domain/usecases/passwordUsecase';
import { toUserStatusText } from '@/core/presenter/transfer/toActivities';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export function useSetting() {
  const { userStore, presentationStore } = useStores();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [settingViewType, setSettingViewType] = useState<ViewModel.SettingViewType>(
    (location.state as { viewType?: ViewModel.SettingViewType })?.viewType || ViewModel.SettingViewType.MAIN,
  );
  const [prePassword, setPrePassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [exitViewType, setExitViewType] = useState<string>('exit');
  const [isPinCheckFailed, setIsPinCheckFailed] = useState<boolean>(false);
  const [pinStep, setPinStep] = useState<'CHECK' | 'NEW' | 'CONFIRM'>('CHECK');
  const [newPinNumber, setNewPinNumber] = useState<string>('');
  const [isNotMatchPin, setIsNotMatchPin] = useState<boolean>(false);
  const [displayPin, setDisplayPin] = useState<string>('');
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);
  const [isPwdCheckFailedModalOpen, setIsPwdCheckFailedModalOpen] = useState<boolean>(false);
  const [isPwdChangeSuccessModalOpen, setIsPwdChangeSuccessModalOpen] = useState<boolean>(false);
  const [isPwdChangeFailedModalOpen, setIsPwdChangeFailedModalOpen] = useState<boolean>(false);
  const [isPinChangeSuccessModalOpen, setIsPinChangeSuccessModalOpen] = useState<boolean>(false);
  const [isWithdrawConfirmModalOpen, setIsWithdrawConfirmModalOpen] = useState<boolean>(false);
  const [isWithdrawCompleteModalOpen, setIsWithdrawCompleteModalOpen] = useState<boolean>(false);
  const [currentPin, setCurrentPin] = useState<string>('');
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [, setNewPin] = useState<string>('');
  const [, setConfirmPin] = useState<string>('');
  const isNotMatchPassword = newPassword !== confirmPassword;
  // 영문, 숫자, 특수문자 포함 여부 검사
  const hasLetter = newPassword ? /[a-zA-Z]/.test(newPassword) : false;
  const hasNumber = newPassword ? /[0-9]/.test(newPassword) : false;
  const hasSpecial = newPassword ? /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) : false;
  const isCheckPasswordDisabled =
    (newPassword?.length ?? 0) < MIN_PASSWORD_LENGTH_FOR_COMPLETE ||
    (newPassword?.length ?? 0) > MAX_PASSWORD_LENGTH_FOR_COMPLETE ||
    !hasLetter ||
    !hasNumber ||
    !hasSpecial;
  const isPrePasswordCheckedBtn = prePassword.length === 0;
  const navigate = useNavigate();

  console.log('isNotMatchPassword', isNotMatchPassword);
  console.log('isCheckPasswordDisabled', isCheckPasswordDisabled);

  useEffect(() => {
    const checkAuth = async () => {
      await checkSessionAndRedirect();
    };

    checkAuth();

    // beforeinstallprompt 이벤트 핸들러 등록
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstallPrompt(e); // 설치 프롬프트 저장
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);

    window.addEventListener('beforeinstallprompt', () => {
      // console.log('beforeinstallprompt fired:', event);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    };
  }, []);

  useEffect(() => {
    if (settingViewType === ViewModel.SettingViewType.MAIN) {
      setPrePassword('');
      setNewPassword('');
      setConfirmPassword('');
      setDisplayPin('');
      setPinStep('CHECK');
      setIsPinCheckFailed(false);
      setNewPinNumber('');
      setIsNotMatchPin(false);
    }

    if (
      settingViewType === ViewModel.SettingViewType.MANAGE_WALLET_BALANCE &&
      toUserStatusText(userStore.user?.status ?? 0) === ViewModel.UserStatus.ADMIN
    ) {
      tryFetchAdminWalletBalance(userStore.user?.address ?? '');
    }
  }, [pinStep, settingViewType, userStore.user?.address, userStore.user?.status]);

  /** 계정 관리 버튼 토글 이벤트 */
  const onClickToggleBtn = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  /** 로그아웃 버튼 이벤트 - 모달 열기 */
  const onClickLogoutBtn = useCallback(() => {
    setIsLogoutModalOpen(true);
  }, []);

  /** 로그아웃 모달 닫기 */
  const onClickLogoutModalClose = useCallback(() => {
    setIsLogoutModalOpen(false);
  }, []);

  /** 로그아웃 모달 확인 */
  const onClickLogoutModalConfirm = useCallback(async () => {
    const isSuccess = await tryLogout();

    if (isSuccess.succed) {
      setIsLogoutModalOpen(false);
      presentationStore.clearStoreData(); // 프레젠테이션 스토어 데이터 초기화
      userStore.clearUserData(); // 유저 스토어 데이터 초기화
      navigate(RouterPath.LOGIN, { replace: true });
    }
  }, [presentationStore, userStore, navigate]);

  const onChangeSettingViewType = useCallback((type: ViewModel.SettingViewType) => {
    if (type === ViewModel.SettingViewType.MAIN) setSettingViewType(ViewModel.SettingViewType.MAIN);
    if (type === ViewModel.SettingViewType.PWD_SETTING) {
      setSettingViewType(ViewModel.SettingViewType.PWD_SETTING);
      setPrePassword('');
    }
    if (type === ViewModel.SettingViewType.CHECK_PWD_SETTING) {
      setSettingViewType(ViewModel.SettingViewType.CHECK_PWD_SETTING);
      setNewPassword('');
      setConfirmPassword('');
    }
    if (type === ViewModel.SettingViewType.PIN_SETTING) setSettingViewType(ViewModel.SettingViewType.PIN_SETTING);
    if (type === ViewModel.SettingViewType.EXIT) setSettingViewType(ViewModel.SettingViewType.EXIT);
    if (type === ViewModel.SettingViewType.MANAGE_WALLET_BALANCE)
      setSettingViewType(ViewModel.SettingViewType.MANAGE_WALLET_BALANCE);
  }, []);

  const onChangePassword = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    if (value.length === 0) {
      // setIsNewPasswordValid(true);
      setPrePassword('');
      return;
    }

    const truncated = value.slice(0, MAX_PASSWORD_LENGTH);

    setPrePassword(truncated);

    if (truncated.length === 0) setIsConfirmPasswordValid(false);
  }, []);

  const onBlurPassword = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    if (value.length === 0) setIsNewPasswordValid(false);

    // 8자 이상 20자 이하 검사
    const isLengthValid = value.length >= 8 && value.length <= 20;
    // 영문, 숫자, 특수문자 포함 여부 검사
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const isValid = isLengthValid && hasLetter && hasNumber && hasSpecial;

    if (!isValid && value.length !== 0) setIsConfirmPasswordValid(true);
    else setIsConfirmPasswordValid(false);
  }, []);

  const onClickPwdNextBtn = useCallback(async () => {
    const encryptedPassword = await encryptPassword(userStore.user?.phone ?? '', prePassword);
    const isSuccess = await tryCheckPassword(userStore.user?.id ?? '', encryptedPassword);

    if (isSuccess.succed) {
      onChangeSettingViewType(ViewModel.SettingViewType.CHECK_PWD_SETTING);
    } else {
      setIsPwdCheckFailedModalOpen(true);
    }
  }, [userStore.user?.phone, userStore.user?.id, prePassword, onChangeSettingViewType]);

  /** 비밀번호 확인 실패 모달 닫기 */
  const onClickPwdCheckFailedModalClose = useCallback(() => {
    setIsPwdCheckFailedModalOpen(false);
  }, []);

  const onChangeNewPassword = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const truncated = value.slice(0, MAX_PASSWORD_LENGTH);

    setNewPassword(truncated);
  }, []);

  const onChangeConfirmPassword = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      if (isCheckPasswordDisabled) {
        e.preventDefault();
        return;
      }

      const value = e.currentTarget.value;
      const truncated = value.slice(0, MAX_PASSWORD_LENGTH);

      setConfirmPassword(truncated);

      if (truncated.length === 0) setIsConfirmPasswordValid(false);
    },
    [isCheckPasswordDisabled],
  );

  const onBlurNewPassword = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    if (value.length === 0) setIsConfirmPasswordValid(false);

    // 8자 이상 20자 이하 검사
    const isLengthValid = value.length >= 8 && value.length <= 20;
    // 영문, 숫자, 특수문자 포함 여부 검사
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const isValid = isLengthValid && hasLetter && hasNumber && hasSpecial;

    if (!isValid && value.length !== 0) setIsNewPasswordValid(true);
    else setIsNewPasswordValid(false);
  }, []);

  const onKeyDownCheckNewPassword = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isCheckPasswordDisabled) {
        e.preventDefault();
      }
    },
    [isCheckPasswordDisabled],
  );

  const onClickCompleteBtn = useCallback(async () => {
    const encryptedPrePassword = await encryptPassword(userStore.user?.phone ?? '', prePassword);
    const encryptedNewPassword = await encryptPassword(userStore.user?.phone ?? '', newPassword);

    const isSuccess = await tryChangePassword(userStore.user?.id ?? '', encryptedPrePassword, encryptedNewPassword);
    if (isSuccess.succed) {
      setIsPwdChangeSuccessModalOpen(true);
    } else {
      setIsPwdChangeFailedModalOpen(true);
    }
  }, [userStore.user?.phone, userStore.user?.id, prePassword, newPassword]);

  /** 비밀번호 변경 성공 모달 확인 */
  const onClickPwdChangeSuccessModalConfirm = useCallback(() => {
    setIsPwdChangeSuccessModalOpen(false);
    onChangeSettingViewType(ViewModel.SettingViewType.MAIN);
  }, [onChangeSettingViewType]);

  /** 비밀번호 변경 실패 모달 확인 */
  const onClickPwdChangeFailedModalConfirm = useCallback(() => {
    setIsPwdChangeFailedModalOpen(false);
  }, []);

  /** PIN 변경 성공 모달 확인 */
  const onClickPinChangeSuccessModalConfirm = useCallback(() => {
    setIsPinChangeSuccessModalOpen(false);
    onChangeSettingViewType(ViewModel.SettingViewType.MAIN);
  }, [onChangeSettingViewType]);

  const onClickCheckbox = () => {
    setIsChecked(!isChecked);
  };

  /** 탈퇴 버튼 클릭 - 확인 모달 열기 */
  const onClickWithdrawBtn = () => {
    setIsWithdrawConfirmModalOpen(true);
  };

  /** 탈퇴 확인 모달 닫기 */
  const onClickWithdrawConfirmModalClose = useCallback(() => {
    setIsWithdrawConfirmModalOpen(false);
  }, []);

  /** 탈퇴 확인 모달 - 예 클릭 */
  const onClickWithdrawConfirmModalYes = useCallback(async () => {
    setIsWithdrawConfirmModalOpen(false);
    const isSuccess = await tryWithdraw(userStore.user?.id ?? '');
    if (isSuccess.succed) {
      userStore.setIsLogout(true);
      setIsWithdrawCompleteModalOpen(true);
    }
  }, [userStore]);

  /** 탈퇴 완료 모달 확인 */
  const onClickWithdrawCompleteModalConfirm = useCallback(() => {
    setIsWithdrawCompleteModalOpen(false);
    navigate(RouterPath.LOGIN);
  }, [navigate]);

  /** 숫자 버튼 클릭 이벤트 */
  const onClickNumberBtn = useCallback(
    async (number: string) => {
      if (displayPin.length < MAX_PIN_LENGTH) {
        const updatedPin = displayPin + number;

        setDisplayPin(updatedPin);

        if (updatedPin.length === MAX_PIN_LENGTH) {
          const encryptedPin = await encryptPassword(userStore.user?.phone ?? '', updatedPin);
          const currentEncryptedPin = await encryptPassword(userStore.user?.phone ?? '', currentPin);
          const newEncryptedPin = await encryptPassword(userStore.user?.phone ?? '', newPinNumber);
          switch (pinStep) {
            case 'CHECK': {
              const isSuccess = await tryPinCheck(userStore.user?.id ?? '', encryptedPin);
              if (isSuccess.succed) {
                setCurrentPin(updatedPin);
                setPinStep('NEW');
                setDisplayPin('');
              } else {
                setIsPinCheckFailed(true);
                setDisplayPin('');
              }
              break;
            }
            case 'NEW': {
              setNewPin(updatedPin);
              setNewPinNumber(updatedPin);
              setPinStep('CONFIRM');
              setDisplayPin('');
              break;
            }
            case 'CONFIRM': {
              if (updatedPin !== newPinNumber) {
                setIsNotMatchPin(true);
                setConfirmPin('');
                setDisplayPin('');
              } else {
                const isSuccess = await trySetPin(
                  userStore.user?.id ?? '',
                  userStore.user?.phone ?? '',
                  currentEncryptedPin,
                  newEncryptedPin,
                );
                if (isSuccess.succed) {
                  setPinStep('CHECK');
                  setCurrentPin('');
                  setNewPin('');
                  setConfirmPin('');
                  setDisplayPin('');
                  setNewPinNumber('');
                  setIsNotMatchPin(false);
                  setIsPinChangeSuccessModalOpen(true);
                } else {
                  setConfirmPin('');
                  setDisplayPin('');
                }
              }
              break;
            }
          }
        }
      }
    },
    [displayPin, currentPin, newPinNumber, pinStep, userStore.user?.id, userStore.user?.phone, onChangeSettingViewType],
  );

  /** 삭제 버튼 클릭 이벤트 */
  const OnClickDeleteBtn = useCallback(() => {
    setDisplayPin(displayPin.slice(0, -1));

    switch (pinStep) {
      case 'CHECK':
        // 기존 로직 유지
        break;
      case 'NEW':
        setNewPin(displayPin.slice(0, -1));
        break;
      case 'CONFIRM':
        setConfirmPin(displayPin.slice(0, -1));
        break;
    }
  }, [displayPin, pinStep]);

  /** 전체 삭제 버튼 클릭 이벤트 */
  const onClickAllClearBtn = useCallback(() => {
    setDisplayPin('');

    switch (pinStep) {
      case 'CHECK':
        // 기존 로직 유지
        break;
      case 'NEW':
        setNewPin('');
        break;
      case 'CONFIRM':
        setConfirmPin('');
        break;
    }
  }, [pinStep]);

  const onMoveSettingMainView = useCallback(() => {
    setSettingViewType(ViewModel.SettingViewType.MAIN);
  }, []);

  const onClickAddHomeBtn = useCallback(async () => {
    if (installPrompt) {
      const isOk = await showAlertPopup('홈화면에 추가하시겠습니까?', false, true);
      if (isOk) {
        installPrompt.prompt(); // 설치 프롬프트 표시
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
          await showConfirmPopup('홈화면에 추가되었습니다.', true);
        } else {
          showAlertPopup('홈 화면 추가를 취소하셨습니다.', true);
        }
        setInstallPrompt(null); // 설치 후 상태 초기화
      }
    } else {
      showAlertPopup('홈화면에 추가할 수 없습니다.', true);
    }
  }, [installPrompt]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      const promptEvent = event as BeforeInstallPromptEvent; // 명시적 캐스팅
      promptEvent.preventDefault(); // 기본 동작 방지
      setInstallPrompt(promptEvent); // 상태에 저장
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const onClickRefreshAdminWalletBtn = useCallback(async () => {
    const isSuccess = await tryFetchAdminWalletBalance(presentationStore.adminWalletBalance.address);

    if (!isSuccess.succed) {
      showAlertPopup(replaceNewLineToTag(isSuccess.errorMessage ?? ''), true, false, isSuccess.errorMessage ?? '');
    }
  }, [presentationStore.adminWalletBalance.address]);

  return {
    settingViewType,
    isOpen,
    prePassword,
    isPrePasswordCheckedBtn,
    newPassword,
    confirmPassword,
    isNewPasswordValid,
    isNotMatchPassword,
    isCheckPasswordDisabled,
    isChecked,
    lastLoginTime: userStore.user?.lastLoginTime,
    pin: displayPin,
    isPinCheckFailed,
    pinStep,
    newPinNumber,
    isNotMatchPin,
    isConfirmPasswordValid,
    userStatus: userStore.user?.status,
    adminWalletInfo: presentationStore.adminWalletBalance,
    exitViewType,
    onClickNumberBtn,
    OnClickDeleteBtn,
    onClickAllClearBtn,
    isPinChangeSuccessModalOpen,
    onClickPinChangeSuccessModalConfirm,
    onChangeSettingViewType,
    onClickToggleBtn,
    onClickLogoutBtn,
    isLogoutModalOpen,
    onClickLogoutModalClose,
    onClickLogoutModalConfirm,
    onChangePassword,
    onClickPwdNextBtn,
    isPwdCheckFailedModalOpen,
    onClickPwdCheckFailedModalClose,
    onChangeNewPassword,
    onChangeConfirmPassword,
    onBlurNewPassword,
    onKeyDownCheckNewPassword,
    onClickCompleteBtn,
    isPwdChangeSuccessModalOpen,
    onClickPwdChangeSuccessModalConfirm,
    isPwdChangeFailedModalOpen,
    onClickPwdChangeFailedModalConfirm,
    onClickCheckbox,
    onClickWithdrawBtn,
    isWithdrawConfirmModalOpen,
    isWithdrawCompleteModalOpen,
    onClickWithdrawConfirmModalClose,
    onClickWithdrawConfirmModalYes,
    onClickWithdrawCompleteModalConfirm,
    onMoveSettingMainView,
    onClickAddHomeBtn,
    onBlurPassword,
    onClickRefreshAdminWalletBtn,
    onChangeExitViewType: setExitViewType,
  };
}
